import React, { useState } from 'react';
import { adminService, sessionService, attendanceService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import AttendanceModal from '../components/admin/AttendanceModal';
import {
  Video, Mic, MessageCircle, Search, RefreshCw,
  CheckCircle2, XCircle, Clock, Loader2, FileText,
  RotateCcw, ChevronDown, User, Calendar, ClipboardList,
} from 'lucide-react';
import toast from 'react-hot-toast';

const SESSION_TYPES = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };

const STATUS_STYLES = {
  COMPLETED:  { bg: 'bg-forest-50',  text: 'text-forest-700',  border: 'border-forest-200',  label: 'Completed'  },
  LIVE:       { bg: 'bg-green-50',   text: 'text-green-700',   border: 'border-green-200',   label: 'Live'       },
  SCHEDULED:  { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    label: 'Scheduled'  },
  CONFIRMED:  { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   label: 'Confirmed'  },
  CANCELLED:  { bg: 'bg-rust-50',    text: 'text-rust-500',    border: 'border-rust-200',    label: 'Cancelled'  },
  NO_SHOW:    { bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    label: 'No Show'    },
};

const TRANSCRIPT_STYLES = {
  COMPLETED:  { icon: CheckCircle2, color: 'text-forest-600', label: 'Ready'       },
  FAILED:     { icon: XCircle,      color: 'text-rust-500',   label: 'Failed'      },
  PROCESSING: { icon: Loader2,      color: 'text-amber-600',  label: 'Processing'  },
  PENDING:    { icon: Clock,        color: 'text-forest-400', label: 'Pending'     },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.SCHEDULED;
  return (
    <span className={`text-[10px] font-semibold border px-2.5 py-0.5 rounded-full ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
};

const TranscriptBadge = ({ transcript, sessionId, onRetry }) => {
  const [retrying, setRetrying] = useState(false);
  if (!transcript) return <span className="text-[10px] text-forest-300">—</span>;

  // Self-heal: if content exists treat as COMPLETED regardless of stuck status
  const effectiveStatus = (transcript.pdfUrl || transcript.rawText) ? 'COMPLETED' : transcript.status;
  const ts   = TRANSCRIPT_STYLES[effectiveStatus] ?? TRANSCRIPT_STYLES.PENDING;
  const Icon = ts.icon;

  const handleRetry = async (e) => {
    e.stopPropagation();
    setRetrying(true);
    try {
      await sessionService.retryTranscript(sessionId);
      toast.success('Transcript retry started');
      onRetry?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Retry failed');
    } finally { setRetrying(false); }
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className={`flex items-center gap-1 text-[10px] font-medium ${ts.color}`}>
        <Icon size={10} className={effectiveStatus === 'PROCESSING' ? 'animate-spin' : ''} />
        {ts.label}
      </span>
      {effectiveStatus === 'FAILED' && (
        <button onClick={handleRetry} disabled={retrying}
          className="text-[10px] text-amber-600 border border-amber-200 rounded-full px-1.5 py-0.5 hover:bg-amber-50 transition-colors disabled:opacity-50">
          {retrying ? '...' : <RotateCcw size={9} />}
        </button>
      )}
      {transcript.pdfUrl && (
        <a href={transcript.pdfUrl} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-[10px] text-forest-600 border border-forest-200 rounded-full px-1.5 py-0.5 hover:bg-forest-50 transition-colors">
          <FileText size={9} />
        </a>
      )}
    </div>
  );
};

// ── Expanded row detail ───────────────────────────────────────
const SessionDetail = ({ session, onViewAttendance }) => {
  const date = session.scheduledAt
    ? new Date(session.scheduledAt).toLocaleDateString('en-GH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '—';
  const time = session.scheduledAt
    ? new Date(session.scheduledAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
    : '—';

  const effectiveTranscriptStatus = session.transcript
    ? ((session.transcript.pdfUrl || session.transcript.rawText) ? 'COMPLETED' : session.transcript.status)
    : null;

  return (
    <div className="px-5 pb-5 pt-3 bg-cream-50 border-t border-cream-100 space-y-4">

      {/* Meta grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <p className="text-forest-400 uppercase tracking-wider text-[10px] font-semibold mb-1">Date & Time</p>
          <p className="text-forest-800">{date}</p>
          <p className="text-forest-500">{time}</p>
        </div>
        <div>
          <p className="text-forest-400 uppercase tracking-wider text-[10px] font-semibold mb-1">Duration</p>
          <p className="text-forest-800">{session.duration ? `${session.duration} min` : '—'}</p>
          {session.startedAt && session.endedAt && (
            <p className="text-forest-400">
              {new Date(session.startedAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
              {' → '}
              {new Date(session.endedAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div>
          <p className="text-forest-400 uppercase tracking-wider text-[10px] font-semibold mb-1">Expert</p>
          <p className="text-forest-800">{session.expert?.name || '—'}</p>
        </div>
        <div>
          <p className="text-forest-400 uppercase tracking-wider text-[10px] font-semibold mb-1">Client(s)</p>
          {session.clients?.length
            ? session.clients.map((c, i) => <p key={i} className="text-forest-800">{c.name}</p>)
            : <p className="text-forest-400">—</p>}
        </div>
        {session.transcript?.wordCount && (
          <div>
            <p className="text-forest-400 uppercase tracking-wider text-[10px] font-semibold mb-1">Word Count</p>
            <p className="text-forest-800">{session.transcript.wordCount.toLocaleString()} words</p>
          </div>
        )}
      </div>

      {/* Attendance */}
      <div>
        <button
          onClick={(e) => { e.stopPropagation(); onViewAttendance(session); }}
          className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 hover:bg-white px-3 py-1.5 rounded-full transition-colors"
        >
          <ClipboardList size={12} /> View Attendance
        </button>
      </div>

      {/* Video recording */}
      {session.recordingUrl && (
        <div>
          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-2">Recording</p>
          <video
            src={session.recordingUrl}
            controls
            className="w-full max-w-2xl rounded-xl overflow-hidden bg-black"
            style={{ maxHeight: '320px' }}
          />
        </div>
      )}

      {/* Transcript text */}
      {effectiveTranscriptStatus === 'COMPLETED' && session.transcript?.rawText && (
        <div>
          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-2">Transcript</p>
          <div className="bg-white border border-cream-200 rounded-xl p-4 text-sm text-forest-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
            {session.transcript.rawText}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminSessionsPage = () => {
  const [search,       setSearch]       = useState('');
  const [appliedSearch,setAppliedSearch]= useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded,     setExpanded]     = useState(null);
  const [page,         setPage]         = useState(1);
  const [attendanceSession, setAttendanceSession] = useState(null);

  const { data, loading, error, refetch } = useApi(
    () => adminService.getAllSessions({ page, limit: 25, status: statusFilter || undefined, search: appliedSearch || undefined }),
    [page, statusFilter, appliedSearch]
  );

  const sessions = data?.sessions ?? [];
  const total    = data?.total ?? 0;
  const pages    = Math.ceil(total / 25);

  const TypeIcon = (type) => {
    const I = SESSION_TYPES[type] ?? Video;
    return <I size={13} className="text-forest-400" />;
  };

  return (
    <DashboardLayout title="All Sessions">
      <div className="space-y-5 max-w-6xl">

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
            <input type="text" placeholder="Search expert or client..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setAppliedSearch(search); setPage(1); } }}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => { setAppliedSearch(search); setPage(1); }}
            className="px-5 py-2.5 rounded-xl bg-forest-800 text-cream-50 text-sm font-medium hover:bg-forest-700 transition-colors">
            Search
          </button>
          <button onClick={refetch}
            className="p-2.5 rounded-xl border border-cream-200 text-forest-500 hover:bg-cream-50 transition-colors">
            <RefreshCw size={15} />
          </button>
        </div>

        <p className="text-sm text-forest-500">{loading ? 'Loading...' : `${total} session${total !== 1 ? 's' : ''}`}</p>

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <XCircle size={15} /> Could not load sessions.
          </div>
        )}

        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
            <div className="col-span-1">Type</div>
            <div className="col-span-3">Expert</div>
            <div className="col-span-3">Client(s)</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Transcript</div>
            <div className="col-span-1" />
          </div>

          {loading ? (
            <div className="divide-y divide-cream-50">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-14 px-5 flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-cream-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-cream-200 rounded w-1/3" />
                    <div className="h-2.5 bg-cream-100 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-16">
              <Video size={32} className="text-forest-200 mx-auto mb-3" />
              <p className="font-serif text-xl text-forest-600">No sessions found</p>
              <p className="text-sm text-forest-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div>
              {sessions.map(s => (
                <div key={s.id} className="border-b border-cream-50 last:border-0">
                  {/* Row */}
                  <div
                    className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-cream-50/50 transition-colors cursor-pointer"
                    onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                  >
                    <div className="col-span-1 flex items-center gap-1.5">
                      {TypeIcon(s.sessionType)}
                      <span className="text-[9px] text-forest-400 hidden sm:block">{s.sessionType}</span>
                    </div>
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 text-[10px] font-bold flex-shrink-0">
                        {s.expert?.name?.[0] ?? <User size={10} />}
                      </div>
                      <span className="text-sm text-forest-800 truncate">{s.expert?.name || '—'}</span>
                    </div>
                    <div className="col-span-3 min-w-0">
                      {s.clients?.length ? (
                        <div className="space-y-0.5">
                          {s.clients.slice(0, 2).map((c, i) => (
                            <p key={i} className="text-sm text-forest-700 truncate">{c.name}</p>
                          ))}
                          {s.clients.length > 2 && (
                            <p className="text-[10px] text-forest-400">+{s.clients.length - 2} more</p>
                          )}
                        </div>
                      ) : <span className="text-forest-300 text-sm">—</span>}
                    </div>
                    <div className="col-span-2">
                      {s.scheduledAt ? (
                        <div>
                          <p className="text-xs text-forest-700">{new Date(s.scheduledAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-[10px] text-forest-400">{new Date(s.scheduledAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      ) : <span className="text-forest-300 text-xs">—</span>}
                    </div>
                    <div className="col-span-1"><StatusBadge status={s.status} /></div>
                    <div className="col-span-1">
                      <TranscriptBadge transcript={s.transcript} sessionId={s.id} onRetry={refetch} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <ChevronDown size={14} className={`text-forest-300 transition-transform ${expanded === s.id ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expanded === s.id && <SessionDetail session={s} onViewAttendance={setAttendanceSession} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
              ← Prev
            </button>
            <span className="text-sm text-forest-500">Page {page} of {pages}</span>
            <button disabled={page === pages} onClick={() => setPage(p => p + 1)}
              className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
              Next →
            </button>
          </div>
        )}
      </div>

      {attendanceSession && (
        <AttendanceModal
          title={`${attendanceSession.expert?.name || 'Session'} — ${attendanceSession.clients?.map(c => c.name).join(', ') || 'session'}`}
          roomLabel={attendanceSession.scheduledAt ? new Date(attendanceSession.scheduledAt).toLocaleDateString('en-GH', { dateStyle: 'long' }) : undefined}
          fetchFn={() => attendanceService.getForSession(attendanceSession.id)}
          syncFn={() => attendanceService.syncSession(attendanceSession.id)}
          onClose={() => setAttendanceSession(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminSessionsPage;